import { match, P } from 'ts-pattern'
import { GenerationModelType } from 'src/services/apollo/generated/graphql'
import type { MODEL_TYPE_OPTION_ALIAS } from './constants'

export type ModelFilterValue = {
  /** All, Lora, Model */
  type?: string
  /** Lora / Model is SD1.5, SDXL, DiT.2 or DiT.1 */
  modelTypes?: MODEL_TYPE_OPTION_ALIAS[]
  /** Lora only. Lora can be used on SD1.5, SDXL, DiT.2 or DiT.1 */
  baseModelTypes?: MODEL_TYPE_OPTION_ALIAS[]
  /** Trained Lora only. TODO: update this field to support multiple choices. */
  baseModelId?: string
  /**
   * Lora only
   * 'pixai' or 'external'
   */
  source?: string
  /** Lora only */
  category?: string
  time?: string
}

const ALL_MODEL = new Set([
  GenerationModelType.SdV1Model,
  GenerationModelType.SdxlModel,
  GenerationModelType.Mmdit26AModel,
  GenerationModelType.Dit7Model,
])

/**
 * Note: Consider removing this condition check
 * Since we're no longer using Lora type for filtering
 * It's replaced by loraBaseModelTypes
 */
const ALL_LORA = new Set([
  GenerationModelType.Lora,
  GenerationModelType.SdxlLora,
  GenerationModelType.UserLora,
  GenerationModelType.UserSdxlLora,
  GenerationModelType.Dit7Lora,
])
const ALL_VIDEO = new Set([
  GenerationModelType.VideoModel,
  GenerationModelType.VideoModelLora,
])

const ALL_SDV1 = new Set([
  GenerationModelType.SdV1Model,
  GenerationModelType.Lora,
  GenerationModelType.UserLora,
])
const ALL_SDXL = new Set([
  GenerationModelType.SdxlModel,
  GenerationModelType.SdxlLora,
  GenerationModelType.UserSdxlLora,
])
const ALL_DIT7B = new Set([
  GenerationModelType.Dit7Lora,
  GenerationModelType.Dit7Model,
])
const ALL_MMDIT26A = new Set([GenerationModelType.Mmdit26AModel])
const ALL_ANY_LORA = new Set([
  GenerationModelType.AnyNonUserLora,
  GenerationModelType.AnyUserLora,
  GenerationModelType.AnyLora,
])

function intersectOf<T>(setA: Set<T>, setB: Set<T>) {
  return setA.intersection(setB).size > 0
}

function normalizeValueToSet(value?: string | string[]): Set<string> {
  if (Array.isArray(value)) {
    return new Set(value)
  }
  return new Set(value ? [value] : [])
}

export { ModelTypeToModelFilters as modelTypeToModelFilters }
/** Convert type (external state) to filters with alias (internal state) */
function ModelTypeToModelFilters(
  /** GenerationModelType enum string */
  modelType: undefined | string | string[],
  /** GenerationModelType enum string */
  baseModelTypes?: undefined | string | string[],
): Pick<ModelFilterValue, 'type' | 'modelTypes' | 'baseModelTypes' | 'source'> {
  const modelTypesSet = normalizeValueToSet(modelType)
  const baseModelTypesSet = normalizeValueToSet(baseModelTypes)

  return {
    type: match(modelTypesSet)
      // Handle empty
      .when(
        v => v.size < 1,
        () => '',
      )
      .when(
        v => v.isSubsetOf(ALL_MODEL) || v.has(GenerationModelType.AnyModel),
        () => 'model',
      )
      .when(
        v =>
          v.isSubsetOf(ALL_LORA) ||
          v.isSubsetOf(ALL_ANY_LORA) ||
          v.has(GenerationModelType.AnyLora),
        () => 'lora',
      )
      .when(
        v => v.isSubsetOf(ALL_VIDEO),
        () => 'video',
      )
      .otherwise(() => ''),
    modelTypes: mapGenerationModelTypesToTypeAlias(modelTypesSet),
    baseModelTypes: mapGenerationModelTypesToTypeAlias(baseModelTypesSet),
    source: match(modelTypesSet)
      .when(
        v => v.has(GenerationModelType.AnyUserLora),
        () => 'pixai',
      )
      .when(
        v => v.has(GenerationModelType.AnyNonUserLora),
        () => 'external',
      )
      .otherwise(() => ''),
  }
}

/** Normalize [], [''] to '' */
function normalizeEmptySelection(
  input?: MODEL_TYPE_OPTION_ALIAS | MODEL_TYPE_OPTION_ALIAS[],
) {
  if (Array.isArray(input)) {
    if (input.length === 0) return ''
    if (input.length === 1 && input[0] === '') return ''
  }

  return input
}

export { ModelFiltersToModelType as modelFiltersToModelType }
/** Convert filters to type */
function ModelFiltersToModelType(
  filters: Pick<
    ModelFilterValue,
    'type' | 'modelTypes' | 'baseModelTypes' | 'baseModelId' | 'source'
  >,
): {
  type: '' | GenerationModelType[]
  loraBaseModelTypes: '' | GenerationModelType[]
} {
  const ALL_TYPE = P.optional('')
  const ALL_BASE = P.optional('')
  const ALL_MODEL = new Set([
    GenerationModelType.SdV1Model,
    GenerationModelType.SdxlModel,
    GenerationModelType.Mmdit26AModel,
    GenerationModelType.Dit7Model,
  ])

  const normalizedFilters = {
    ...filters,
    modelTypes: normalizeEmptySelection(filters.modelTypes),
    baseModelTypes: normalizeEmptySelection(filters.baseModelTypes),
  }
  const type = match(normalizedFilters)
    .returnType<'' | GenerationModelType[]>()
    .with({ type: ALL_TYPE }, () => '')
    /** model */
    .with({ type: 'model', modelTypes: ALL_BASE }, () => [
      GenerationModelType.AnyModel,
    ])
    .with({ type: 'model' }, ({ modelTypes }) =>
      mapTypeAliasToGenerationModelTypes({ modelTypes }),
    )
    /** lora */
    .with({ type: 'lora', source: ALL_TYPE }, () => [
      GenerationModelType.AnyLora,
    ])
    .with({ type: 'lora', source: 'pixai' }, () => [
      GenerationModelType.AnyUserLora,
    ])
    .with({ type: 'lora', source: 'external' }, () => [
      GenerationModelType.AnyNonUserLora,
    ])
    .with({ type: 'video' }, () => [
      GenerationModelType.VideoModel,
      GenerationModelType.VideoModelLora,
    ])

    .otherwise(() => [])

  // lora only
  const loraBaseModelTypes = match(filters.baseModelTypes)
    .with(P.array(P.string), () => {
      let set = new Set(ALL_MODEL)
      const ALL_SDV1 = new Set([
        GenerationModelType.SdV1Model,
        GenerationModelType.Lora,
        GenerationModelType.UserLora,
      ])
      const ALL_SDXL = new Set([
        GenerationModelType.SdxlModel,
        GenerationModelType.SdxlLora,
        GenerationModelType.UserSdxlLora,
      ])
      const ALL_DIT7B = new Set([
        GenerationModelType.Dit7Lora,
        GenerationModelType.Dit7Model,
      ])
      const ALL_MMDIT26A = new Set([GenerationModelType.Mmdit26AModel])
      if (filters.baseModelTypes) {
        if (!filters.baseModelTypes.includes('sdv1')) {
          set = set.difference(ALL_SDV1)
        }
        if (!filters.baseModelTypes.includes('sdxl')) {
          set = set.difference(ALL_SDXL)
        }
        if (!filters.baseModelTypes.includes('dit7')) {
          set = set.difference(ALL_DIT7B)
        }
        if (!filters.baseModelTypes.includes('mmdit26a')) {
          set = set.difference(ALL_MMDIT26A)
        }
      }
      if (set.size < 1) return ''
      return Array.from(set)
    })
    .otherwise(() => '' as const)

  return {
    type,
    loraBaseModelTypes,
  }
}

/**
 * Auto-normalizer for filter values
 */
export const modelFilterNormalizerHandler: ProxyHandler<ModelFilterValue> = {
  set(target, prop, value, receiver) {
    Reflect.set(target, prop, value, receiver)
    switch (prop) {
      case 'type':
        // Filter `source`, `category`, `baseModelTypes`, `baseModelId` are only applicable to LoRA.
        // So reset them when choosing types other than LoRA.
        if (value !== 'lora') {
          if (target.baseModelId) receiver.baseModelId = ''
          if (target.source) receiver.source = ''
          if (target.category) receiver.category = ''
          if (target.baseModelTypes) receiver.baseModelTypes = ['']

          // Reset modelTypes as well when choosing 'all'
          if (value !== 'model') {
            receiver.modelTypes = ['']
          }
        }
        break

      case 'modelTypes':
        // Update to 'all' if all of 'sdv1' 'sdxl' 'dit7' 'mmdit26a' are selected
        if (
          Array.isArray(value) &&
          value.includes('sdv1') &&
          value.includes('sdxl') &&
          value.includes('dit7') &&
          value.includes('mmdit26a')
        ) {
          receiver.modelTypes = ['']
        }
        break

      case 'baseModelTypes':
        if (Array.isArray(value)) {
          // When a base model type is specified
          if (value[0] !== '') {
            // Only supports lora type
            if (target.type !== 'lora') receiver.type = 'lora'
            // Specified baseModel isn't supported
            if (target.baseModelId && /^\d+$/.test(target.baseModelId))
              receiver.baseModelId = ''
            // dit family baseModel doesn't support baseModelId
            if ((['mmdit26a', 'dit7'] as const).some(t => value.includes(t))) {
              receiver.baseModelId = ''
            }
          }
          // Update to 'all' if all of 'sdv1' 'sdxl' 'dit7' 'mmdit26a' are selected
          if (
            value.includes('sdv1') &&
            value.includes('sdxl') &&
            value.includes('dit7') &&
            value.includes('mmdit26a')
          )
            receiver.baseModelTypes = ['']
        }
        break

      case 'baseModelId':
        /**
         * When enabling `baseModelId` filter
         * - Reset `type` to LoRA,
         * - Reset `source` to User Lora
         * - Reset `baseModelTypes` to 'all' if dit family types are selected
         */
        if (value) {
          if (target.type !== 'lora') receiver.type = 'lora'
          if (target.source !== 'pixai') receiver.source = 'pixai'
          if (
            (['mmdit26a', 'dit7'] as const).some(t =>
              target.baseModelTypes?.includes(t),
            )
          )
            receiver.baseModelTypes = ['']
        }
        break

      case 'source':
        // Reset `type` to LoRA when enabling `source` filter
        if (value) {
          if (target.type !== 'lora') receiver.type = 'lora'
        }
        // Reset `baseModelId` if source isn't `pixai`
        if (!value || value !== 'pixai') {
          if (target.baseModelId) receiver.baseModelId = ''
        }
        break

      // Reset `type` to LoRA when enabling `category` filter
      case 'category':
        if (value && target.type !== 'lora') {
          receiver.type = 'lora'
        }
        break
    }
    return true
  },
}

/** Convert filters with alias (internal state) to type (external state) */
function mapTypeAliasToGenerationModelTypes(args: {
  modelTypes?: MODEL_TYPE_OPTION_ALIAS[] | MODEL_TYPE_OPTION_ALIAS
}): GenerationModelType[] {
  if (!args.modelTypes) return []
  let parsedTypes: string[]

  if (typeof args.modelTypes === 'string') parsedTypes = [args.modelTypes]
  else parsedTypes = args.modelTypes

  return parsedTypes
    .map(type =>
      match(type)
        .with('sdv1', () => GenerationModelType.SdV1Model)
        .with('sdxl', () => GenerationModelType.SdxlModel)
        .with('dit7', () => GenerationModelType.Dit7Model)
        .with('mmdit26a', () => GenerationModelType.Mmdit26AModel)
        .otherwise(() => ''),
    )
    .filter(Boolean) as GenerationModelType[]
}

/**
 *
 * @param actualTypes should be a set of GenerationModelType
 * @returns
 */
function mapGenerationModelTypesToTypeAlias(actualTypes: Set<string>) {
  let modelTypesSet = new Set<MODEL_TYPE_OPTION_ALIAS>()
  if (intersectOf(actualTypes, ALL_SDV1)) modelTypesSet.add('sdv1')
  if (intersectOf(actualTypes, ALL_SDXL)) modelTypesSet.add('sdxl')
  if (intersectOf(actualTypes, ALL_DIT7B)) modelTypesSet.add('dit7')
  if (intersectOf(actualTypes, ALL_MMDIT26A)) modelTypesSet.add('mmdit26a')

  if (modelTypesSet.size < 1) modelTypesSet = new Set([''])

  return Array.from(modelTypesSet)
}
